import React, {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {Box, Table, useTheme} from '@material-ui/core';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import moment from 'moment';
import {
  faEdit,
  faSave,
  faTrash,
  faUserMinus,
} from '@fortawesome/free-solid-svg-icons';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableBody from '@material-ui/core/TableBody';

import PropTypes from 'prop-types';
import {deleteFetch, getFetch, postFetch} from '../../../ApiBackend';
import InactiveActiveSwitchState
from '../../../Components/InactiveActiveSwitchState';
import BoxInfo from '../../../Components/BoxInfo';
import RedIconButton from '../../../core/components/buttons/RedIconButton';
import {StyledTableCell} from '../../../Components/Table';
import Spinner from '../../../Components/Spinner';
import configs from '../../../configs';
import {displayError} from '../../../ui-components/displayMsg';
import RedTextField from '../../../core/components/inputs/RedTextField';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Checkbox,
  Grid,
} from '@material-ui/core';
import ListItemText from '@material-ui/core/ListItemText';
import RedButton from '../../../core/components/buttons/RedButton';
import Autocomplete from '@material-ui/lab/Autocomplete';

/**
 *
 * @param {*} service
 * @param {boolean} globalStart
 * @return {JSX.Element}
 * @constructor
 */
export function EndpointHostsTable({service, globalStart}) {
  const padding = useTheme().spacing(3);
  const [hosts, setHosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [error, setError] = useState(undefined);

  const [keycloak] = useSelector((state) => [
    state.keycloak,
  ]);
  const roles = keycloak.tokenParsed
      .resource_access[configs.KEYCLOAK_CONFIGS.clientId]?.roles || [];
  const canEdit = roles.includes('root') || roles.includes('admin');


  useEffect(() => {
    setLoading(true);
    setError(false);
    getFetch(`service/${service}/endpoint/members`)
        .then((data) => {
          setHosts(data?.hosts ?? []);
          setLoading(false);
          setError(false);
        },
        ).catch(() => {
          setLoading(false);
          setError(true);
        });
  }, [service, globalStart]);

  const handleUpgradeAgents = (selectedVersion, selectedHosts) => {
    Promise.all(selectedHosts.map((clientId) =>
      postFetch(`service/${service}/endpoint/${clientId}/latestVersion`,
          {latestVersion: selectedVersion}),
    )).then(() => {
      setShowUpgradeModal(false);
      // Update the host list if necessary
    });
  };
  const onChangeAgentAutoStart = (agentId) => (_) => {
    const agentInfo = hosts.find((h) => h.clientId === agentId);
    const newAutoStart = !(['true', true].includes(agentInfo.autostart));
    postFetch(`service/${service}/endpoint/${agentId}/autostart`,
        {autoStart: newAutoStart})
        .then(() => {
          setHosts((hosts) => hosts.map((h) => {
            if (h.clientId === agentId) {
              return {...h, autostart: newAutoStart};
            }
            return h;
          }));
        });
  };

  const onChangeAgentStatus = (agentId, newStatus) => (_) => {
    if (!newStatus) {
      const agentInfo = hosts.find((h) => h.clientId === agentId);
      newStatus = agentInfo.status === 'enabled' ? 'disabled' : 'enabled';
    }

    postFetch(`service/${service}/endpoint/${agentId}/status`,
        {status: newStatus})
        .then(() => {
          setHosts((hosts) => hosts.map((h) => {
            if (h.clientId === agentId) {
              return {...h, status: newStatus};
            }
            return h;
          }));
        });
  };

  const onClickDeleteAgent = (agentId) => (_) => {
    const aid = agentId?.replace('service-account-', '');
    deleteFetch(`service/${service}/endpoint/${aid}`).then(() => {
      setHosts((hosts) => hosts.filter((h) => h.clientId !== agentId));
    });
  };

  const columns = [
    {
      props: {style: {textAlign: 'center', width: 0}},
      data_props: {style: {textAlign: 'center', width: 0}},
      title: <></>,
      data: 'status',
      hide: !canEdit,
      render: (status, row) => {
        return <RedIconButton
          onClick={onChangeAgentStatus(row.clientId, 'unlicensed')}
          alwaysTooltip={true}
          TooltipProps={{title: 'Unlicensed agent'}}
          disabled={status === 'unlicensed'}>
          <FontAwesomeIcon icon={faUserMinus}/>
        </RedIconButton>;
      },
    },
    {
      props: {style: {textAlign: 'center', width: 80}},
      data_props: {style: {textAlign: 'center', width: 80}},
      title: <b>Agent Status</b>,
      data: 'status',
      render: (status, row) => {
        const s = status === 'enabled';
        return <InactiveActiveSwitchState
          checked={s}
          sideEffect={onChangeAgentStatus(row.clientId)}
          label={status ? status[0].toUpperCase() + status.slice(1) : 'Unknown'}
          disabled={!canEdit}
        />;
      },
    },
    {
      props: {style: {textAlign: 'center', width: 80}},
      data_props: {style: {textAlign: 'center', width: 80}},
      title: <b>Hostname</b>,
      data: 'hostname',
    },
    {
      props: {style: {textAlign: 'center', width: 80}},
      data_props: {style: {textAlign: 'center', width: 80}},
      title: <b>Interfaces - Mac Address</b>,
      data: 'interfaces',
      render: (interfaces) => {
        return <ul style={{listStyle: 'none', padding: 0}}>
          {interfaces?.map((i) => <li
            key={i.mac_address}>{i.ip} - {i.mac_address}</li>)}
        </ul>;
      },
    },
    {
      props: {style: {textAlign: 'center', width: 80}},
      data_props: {style: {textAlign: 'center', width: 80}},
      title: <b>Agent Id</b>,
      data: 'clientId',
      render: (clientId) => clientId?.replace('service-account-', ''),
    },
    {
      props: {style: {textAlign: 'center', width: 80}},
      data_props: {style: {textAlign: 'center', width: 80}},
      title: <b>AutoStart</b>,
      data: 'autostart',
      render: (autoStart, row) => {
        const as = autoStart === 'true';
        return <InactiveActiveSwitchState
          checked={as}
          disabled={!canEdit}
          sideEffect={onChangeAgentAutoStart(row.clientId)}
        />;
      },
    },
    {
      props: {style: {textAlign: 'center', width: 80}},
      data_props: {style: {textAlign: 'center', width: 80}},
      title: <b>Last Login Time</b>,
      data: 'lastLoginTime',
      render: (time) => {
        if (isNaN(time)) {
          return '-';
        }
        return moment(time * 1000).format('HH:mm:ss DD-MM-YYYY');
      },
    },
    {
      props: {style: {textAlign: 'center', width: 80}},
      data_props: {style: {textAlign: 'center', width: 80}},
      title: <b>Version</b>,
      data: 'version',
    },
    {
      props: {style: {textAlign: 'center', width: 50}},
      data_props: {style: {textAlign: 'center', width: 50}},
      title: <></>,
      data: 'status',
      hide: !canEdit,
      render: (status, row) => {
        return <RedIconButton
          onClick={onClickDeleteAgent(row.clientId)}
          alwaysTooltip={true}
          TooltipProps={{title: 'Delete agent'}}
          disabled={!canEdit || status !== 'unlicensed'}>
          <FontAwesomeIcon icon={faTrash}/>
        </RedIconButton>;
      },
    },
  ];
  return (
    <>
      <BoxInfo
        title={
          <>
            <b>Info Endpoint Agents</b>

          </>
        }>
        <Grid
          item
          xs={12}
          style={{
            paddingLeft: padding / 2,
            paddingRight: padding / 2,
          }}
        >
          <p> Number of Endpoint Agents: {hosts.length}</p>
          <p> Number of Enable Endpoint Agents: {hosts.
              filter((h)=> h.status === 'enabled').length}</p>
          <p> Number of Auto Start Endpoint Agents: {hosts.
              filter((h) => ['true', true].includes(h.autostart)).length}</p>
          <p>
          Number of Endpoint Agents seen today: {
              hosts.filter((h) => {
                return moment.unix(Number(h.lastLoginTime)).isSame(moment(), 'day');
              }).length
            }
          </p>
          <p>
          Number of Endpoint Agents by version:
          </p>
          {hosts.length === 0 && <p>No Endpoint Agents available</p>}
          {hosts.length !== 0 && <Box ml={3}>
            {Object.entries(
                hosts.reduce((acc, h) => {
                  const version = h.version || 'Unknown';
                  acc[version] = (acc[version] || 0) + 1;
                  return acc;
                }, {})
            ).sort().map(([version, count]) => (
              <p key={version} style={{marginRight: 8}}>
                {version}: {count}
              </p>
            ))}
          </Box>}
        </Grid>
      </BoxInfo>
      {!loading && !error && !!hosts.length && (
        <BoxInfo
          title={
            <>
              <b>Agent Endpoints</b>
              <RedIconButton
                TooltipProps={{title: 'Upgrade Agents'}}
                onClick={() => setShowUpgradeModal(true)}
                style={{marginLeft: 8}}
              >
                <FontAwesomeIcon icon={faEdit}/>
              </RedIconButton>
            </>
          }>
          <Table style={{width: '100%', tableLayout: 'fixed'}}>
            <TableHead>
              <TableRow>
                {columns.map((col, index) => {
                  const columnKey = `header${index}`;
                  if (col.hide) return null;
                  return (
                    <StyledTableCell key={columnKey}
                      style={col?.props?.style}>
                      {col.title || ''}
                    </StyledTableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {(loading || error) && (
                <TableRowStyled>
                  <StyledTableCell colSpan={columns.length}>
                    <Spinner/>
                  </StyledTableCell>
                </TableRowStyled>
              )}
              {!loading && !error && !hosts.length && (
                <TableRowStyled>
                  <StyledTableCell colSpan={columns.length}>
                    No Data
                  </StyledTableCell>
                </TableRowStyled>
              )}
              {!loading &&
                !error &&
                !!hosts.length &&
                hosts.map((d, rIndex) => {
                  const row = d || {};
                  const rowKey = `row${rIndex}`;
                  return (
                    <TableRowStyled
                      key={rowKey}
                      className={
                        rIndex % 2 ?
                          'platinum-bg  opacity-30' :
                          'white-bg'
                      }
                    >
                      {columns.map(function(column, cIndex) {
                        if (column.hide) return null;
                        let cellData;
                        if (row[cIndex]) {
                          cellData = row[cIndex];
                        } else if (column.render) {
                          cellData = column.render(row[column.data], row);
                        } else {
                          cellData = row[column.data];
                        }
                        const cellKey = `data_${rIndex}_${cIndex}`;
                        return (
                          <StyledTableCell
                            key={cellKey}
                            style={column?.data_props?.style}
                          >
                            {cellData}
                          </StyledTableCell>
                        );
                      })}
                    </TableRowStyled>
                  );
                })}
            </TableBody>
          </Table>
        </BoxInfo>
      )}
      {loading && <BoxInfo
        title={<><Spinner/> <b>Loading Endpoint Hosts info ... </b></>}/>}
      {showUpgradeModal && (
        <UpgradeAgentsModal
          open={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          hosts={hosts}
          onUpgrade={handleUpgradeAgents}
        />
      )}
    </>);
}

EndpointHostsTable.propTypes = {
  service: PropTypes.any,
  globalStart: PropTypes.bool,
  info: PropTypes.any,
};


/**
 *
 * @param{object} style
 * @param{{}} props
 * @return {JSX.Element}
 * @constructor
 */
function TableRowStyled({style = {}, ...props}) {
  return <TableRow {...props}
    style={{
      border: '1px solid #F9F9F9',
      height: '38px', ...style,
    }}/>;
}

TableRowStyled.propTypes = {
  style: PropTypes.object,
};


/**
 * Component for displaying and editing the global agent version.
 *
 * @param {Object} props
 * @param {boolean} props.canEdit - Whether the user can edit the version.
 * @param {string} props.hostAgentVersion - The current agent version.
 * @param {string} props.agentId - The agent id.
 * @param {string} props.service - The service identifier.
 * @return {JSX.Element}
 */
function HostEndpointAgentVersion({
  canEdit,
  agentId,
  hostAgentVersion,
  service,
}) {
  const [version, setVersion] = useState(hostAgentVersion);
  const [changeAgentVersion, setChangeAgentVersion] = useState(false);
  const [latestVersion, setLatestVersion] = useState('');
  const [submittingNewVersion, setSubmittingNewVersion] = useState(false);

  const onSaveHostAgentVersion = (e) => {
    e.preventDefault();
    setSubmittingNewVersion(true);
    postFetch(`service/${service}/endpoint/${agentId}/latest-version`,
        {latestVersion}).then(() => {
      setSubmittingNewVersion(false);
      setChangeAgentVersion(false);
      setLatestVersion('');
      setVersion(latestVersion);
    }).catch((e) => {
      e.text().then((data) => {
        setSubmittingNewVersion(false);
        displayError('Error setting the host agent version:', data);
      });
    });
  };

  return <div style={{
    display: 'inline-flex',
    alignItems: 'center', justifyContent: 'center',
  }}>
    <span>{version}</span>

    {canEdit && !changeAgentVersion && <RedIconButton
      TooltipProps={{title: 'Change Agent Endpoint Version'}}
      onClick={() => setChangeAgentVersion(true)}
    >
      <FontAwesomeIcon icon={faEdit}/>
    </RedIconButton>}
    {changeAgentVersion && <RedTextField
      variant="outlined"
      size="small"
      placeholder="New Version"
      value={latestVersion}
      disabled={submittingNewVersion}
      onChange={({target: {value}}) => setLatestVersion(value)}
    > </RedTextField>}
    {changeAgentVersion && latestVersion && <RedIconButton
      TooltipProps={{title: 'Set New Version'}}
      onClick={onSaveHostAgentVersion}
    >
      <FontAwesomeIcon icon={faSave}/>
    </RedIconButton>}
  </div>;
}

HostEndpointAgentVersion.propTypes = {
  canEdit: PropTypes.bool,
  hostAgentVersion: PropTypes.string,
  agentId: PropTypes.string,
  service: PropTypes.string,
};


/**
 * Modal dialog for upgrading agent versions on selected hosts.
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the modal is open.
 * @param {function} props.onClose - Function to close the modal.
 * @param {Array} props.hosts - List of host objects.
 * @param {function} props.onUpgrade - Function to handle the upgrade action.
 * @return {JSX.Element}
 */
function UpgradeAgentsModal({open, onClose, hosts, onUpgrade}) {
  const [selectedVersion, setSelectedVersion] = useState('');
  const [selectedHosts, setSelectedHosts] = useState([]);


  const handleUpgrade = () => {
    onUpgrade(selectedVersion, selectedHosts);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Upgrade Agent Version</DialogTitle>
      <DialogContent>
        <Box mb={2}>
          <RedTextField
            placeholder="New Agent Version"
            value={selectedVersion}
            onChange={(e) => setSelectedVersion(e.target.value)}
            fullWidth
            variant={'outlined'}
          />
        </Box>
        <Autocomplete
          multiple
          options={hosts}
          getOptionLabel={(option) => option.hostname}
          value={hosts.filter((host) => selectedHosts.includes(host.clientId))}
          onChange={(_, value) =>
            setSelectedHosts(value.map((host) => host.clientId))}
          renderInput={(params) => (
            <RedTextField
              {...params}
              variant="outlined"
              placeholder="Agent Endpoints to Upgrade"
              fullWidth
            />
          )}
          renderOption={(option, {selected}) => (
            <>
              <Checkbox checked={selected}/>
              <ListItemText primary={option.hostname}/>
            </>
          )}
        />

      </DialogContent>
      <DialogActions>
        <RedButton variant="outlined" onClick={onClose} color="secondary">
          Cancel
        </RedButton>
        <RedButton
          onClick={handleUpgrade}
          variant="outlined"
          disabled={!selectedVersion || selectedHosts.length === 0}
        >
          Upgrade
        </RedButton>
      </DialogActions>;
    </Dialog>
  )
  ;
}

UpgradeAgentsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  hosts: PropTypes.arrayOf(PropTypes.shape({
    clientId: PropTypes.string,
    hostname: PropTypes.string,
  })).isRequired,
  onUpgrade: PropTypes.func.isRequired,
};
